import React, { useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Classes from "../AddAppointment.module.css";

const Overlay = ({ children, onClose }) => (
  <div
    role="dialog"
    aria-modal="true"
    onClick={(e) => { if (e.currentTarget === e.target && onClose) onClose(); }}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.4)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
      zIndex: 1000,
      overflowY: "auto",
    }}
  >
    {children}
  </div>
);

const Card = ({ children }) => (
  <div
    style={{
      width: "clamp(320px, 92vw, 720px)",
      background: "#fff",
      borderRadius: 12,
      padding: "clamp(16px, 2vw, 24px)",
      boxShadow: "0 20px 60px rgba(0,0,0,.20)",
      maxHeight: "86vh",
      overflow: "auto",
      margin: "auto",
    }}
  >
    {children}
  </div>
);

export default function ConfirmBookingModal({
  open,
  onClose,
  draft,
  service, // (unused here, but kept to match props)
  rush,    // (unused here, but kept to match props)
  setRush, // (unused here, but kept to match props)
  computeTotals,
  onConfirm,
  saving = false,
  isEdit = false,
}) {
  // Always call hooks before any return/conditional rendering
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const totals = useMemo(() => {
    // safe default if computeTotals is missing
    return computeTotals?.() || { subtotal: 0, deliveryFee: 0, emergencyFee: 0, total: 0 };
  }, [computeTotals]);

  const { subtotal, deliveryFee, emergencyFee, total } = totals;

  const vList = useMemo(() => {
    const base = draft?.vehicle ? [{ ...draft.vehicle, __idx: 0 }] : [];
    const extras = (draft?.extraVehicles || []).map((v, i) => ({ ...v, __idx: i + 1 }));
    return [...base, ...extras];
  }, [draft]);

  // NOW it's safe to return early
  if (!open) return null;

  return (
    <AnimatePresence>
      <Overlay onClose={onClose}>
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          style={{ width: "100%" }}
          onClick={(e) => e.stopPropagation()}
        >
          <Card>
            <div className={Classes.flexBetween} style={{ marginBottom: 6 }}>
              <h3 className={Classes.title3} style={{ margin: 0 }}>
                {isEdit ? "Update Appointment" : "Create Appointment"}
              </h3>
              <button className={Classes.linkBtn} onClick={onClose} type="button">Close</button>
            </div>
            <h5 className={Classes.text} style={{ marginTop: 2 }}>Review details before confirming.</h5>

            {/* Date/Time */}
            <div className={Classes.textAreaCon} style={{ marginTop: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <h5 className={Classes.label}>Date</h5>
                  <div className={Classes.inputField} style={{ display: "flex", alignItems: "center" }}>
                    {draft?.appointment?.date || "-"}
                  </div>
                </div>
                <div>
                  <h5 className={Classes.label}>Time Slot</h5>
                  <div className={Classes.inputField} style={{ display: "flex", alignItems: "center" }}>
                    {draft?.appointment?.timeSlot || "-"}
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicles */}
            <div className={Classes.textAreaCon} style={{ marginTop: 12 }}>
              <h5 className={Classes.label} style={{ marginBottom: 6 }}>Vehicles</h5>
              {vList.map((v) => (
                <div
                  key={v.__idx}
                  className={Classes.flexBetween}
                  style={{ padding: "6px 0", borderBottom: "1px dashed #eee" }}
                >
                  <div>
                    <strong>{v.make || "-"}</strong> {v.model || ""} {v.license ? `• ${v.license}` : ""}
                  </div>
                  <div>{v.fuelTypeId || "-"} • {v.requestedAmount || 0} gal</div>
                </div>
              ))}
            </div>

            {/* Location */}
            <div className={Classes.textAreaCon} style={{ marginTop: 12 }}>
              <h5 className={Classes.label} style={{ marginBottom: 6 }}>Location</h5>
              <div>
                {(draft?.location?.address1 || "-")},{" "}
                {draft?.location?.city || "-"},{" "}
                {draft?.location?.region || "-"} {draft?.location?.postalCode || ""} {draft?.location?.country || ""}
              </div>
              {(draft?.location?.parkingSpotOrNotes || draft?.location?.gateCode) && (
                <div className={Classes.text} style={{ marginTop: 6 }}>
                  {draft?.location?.parkingSpotOrNotes ? `Notes: ${draft.location.parkingSpotOrNotes}` : ""}
                  {draft?.location?.gateCode ? `  • Gate: ${draft.location.gateCode}` : ""}
                </div>
              )}
            </div>

            {/* Totals */}
            <div className={Classes.textAreaCon} style={{ marginTop: 12 }}>
              <div className={Classes.flexBetween} style={{ padding: "6px 0" }}>
                <div>Fuel Subtotal</div><strong>${subtotal.toFixed(2)}</strong>
              </div>
              <div className={Classes.flexBetween} style={{ padding: "6px 0" }}>
                <div>Delivery Fee</div><strong>${Number(deliveryFee).toFixed(2)}</strong>
              </div>
              <div className={Classes.flexBetween} style={{ padding: "6px 0", borderBottom: "1px dashed #eee" }}>
                <div>Emergency Fee</div><strong>${Number(emergencyFee).toFixed(2)}</strong>
              </div>
              <div className={Classes.flexBetween} style={{ padding: "10px 0" }}>
                <h4 style={{ margin: 0 }}>Total</h4><h4 style={{ margin: 0 }}>${total.toFixed(2)}</h4>
              </div>
            </div>

            {/* Actions */}
            <div className={Classes.navRow}>
              <button type="button" className={Classes.backBtn} onClick={onClose}><h5>Back</h5></button>
              <button type="button" className={Classes.saveBtn} onClick={onConfirm} disabled={saving}>
                <h5>{saving ? "Saving..." : (isEdit ? "Update Appointment" : "Create Appointment")}</h5>
              </button>
            </div>
          </Card>
        </motion.div>
      </Overlay>
    </AnimatePresence>
  );
}
